import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PrimaryButton, SecondaryButton } from "@/components/StandardButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { logger } from "@/utils/logger";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog-mobile-optimized";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Building2, CheckCircle2, Circle } from "lucide-react";
import { generateUniqueSlug } from "@/utils/slugValidation";
import { hapticSuccess, hapticError } from "@/utils/haptics";
import { isMobileDevice } from "@/utils/mobileOptimizations";
import { cn } from "@/lib/utils";

interface CompanySetupWizardProps {
  open: boolean;
  onComplete: () => void;
}

type SetupStep = 'profile' | 'page' | 'notifications' | 'complete';

export const CompanySetupWizard = ({ open, onComplete }: CompanySetupWizardProps) => {
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [currentStep, setCurrentStep] = useState<SetupStep | null>(null);
  const { toast } = useToast();

  const MAX_RETRIES = 3;

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const logSetupFailure = async (error: any, step: SetupStep | null, userId?: string) => {
    try {
      await supabase.from('setup_failures').insert({
        user_id: userId || null,
        error_message: error.message || 'Unknown error',
        error_code: error.code || null,
        retry_count: retryCount,
        step_failed: step || 'unknown',
        metadata: {
          company_name: companyName,
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
        },
      });
    } catch (logError) {
      logger.error('Failed to log setup failure', logError);
    }
  };

  const attemptSetup = async (retryAttempt: number = 0): Promise<boolean> => {
    let userId: string | undefined;
    try {
      setCurrentStep('profile');
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not found. Please try logging in again.");
      }
      
      userId = user.id;

      // Ensure profile exists with correct plan
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id, plan")
        .eq("id", user.id)
        .single();

      if (profileError || !profile) {
        throw new Error("Profile not found. Please try logging out and back in.");
      }

      // Generate unique company slug
      const companySlug = await generateUniqueSlug(companyName.trim(), user.id);

      // Create company and owner membership atomically using SECURITY DEFINER function
      const { data: companyId, error: companyError } = await supabase
        .rpc('create_company_with_owner', {
          p_name: companyName.trim(),
          p_slug: companySlug,
        });

      if (companyError) {
        logger.error('Failed to create company', { error: companyError, userId: user.id });
        throw new Error(`Failed to create company: ${companyError.message}`);
      }

      // Update profile with company name
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ company_name: companyName.trim() })
        .eq("id", user.id);

      if (updateError) throw updateError;

      setCurrentStep('page');

      // Auto-create their ONE feedback page with opinionated defaults
      const pageSlug = await generateUniqueSlug(`${companyName.trim()}-feedback`, user.id);
      
      const { error: pageError } = await supabase
        .from("feedback_pages")
        .insert({
          user_id: user.id,
          company_id: companyId,
          title: `${companyName.trim()} Team Feedback`,
          description: `Share your honest thoughts about team dynamics, collaboration, and workplace culture. Your feedback helps us build a better environment.`,
          slug: pageSlug,
          is_active: true,
        });

      if (pageError) {
        logger.error('Failed to create feedback page', { error: pageError, userId: user.id });
        throw new Error(`Failed to create feedback page: ${pageError.message}`);
      }

      setCurrentStep('notifications');
      // Auto-enable notification preferences
      const { error: notifError } = await supabase
        .from("notification_preferences")
        .insert({
          user_id: user.id,
          new_feedback_enabled: true,
          daily_digest_enabled: false,
          weekly_digest_enabled: true,
          sentiment_alerts_enabled: true,
        });

      // Ignore error if preferences already exist
      if (notifError && !notifError.message.includes('duplicate')) {
        logger.error('Error creating notification preferences', notifError);
      }

      setCurrentStep('complete');
      hapticSuccess();
      toast({
        title: "Setup complete!",
        description: "We created your first feedback page - share the link with your team to start collecting feedback.",
      });

      onComplete();
      return true;
    } catch (error: any) {
      await logSetupFailure(error, currentStep, userId);
      const isTransientError = 
        error.message?.includes('network') ||
        error.message?.includes('timeout') ||
        error.message?.includes('fetch') ||
        error.code === 'PGRST301' || // JWT expired
        error.code === '503'; // Service unavailable

      if (isTransientError && retryAttempt < MAX_RETRIES) {
        // Exponential backoff: 1s, 2s, 4s
        const delayMs = Math.pow(2, retryAttempt) * 1000;
        logger.info(`Setup failed, retrying in ${delayMs}ms (attempt ${retryAttempt + 1}/${MAX_RETRIES})`, error);
        
        setIsRetrying(true);
        setError(`Connection issue. Retrying (${retryAttempt + 1}/${MAX_RETRIES})...`);
        
        await sleep(delayMs);
        setIsRetrying(false);
        
        return attemptSetup(retryAttempt + 1);
      }

      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setRetryCount(0);
    setCurrentStep(null);

    // Blur active input to close mobile keyboard
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (!companyName.trim()) {
      setError("Company name is required");
      return;
    }

    if (companyName.trim().length > 100) {
      setError("Company name must be less than 100 characters");
      return;
    }

    setLoading(true);

    try {
      const success = await attemptSetup(0);
      if (!success) {
        throw new Error("Setup failed after multiple attempts");
      }
    } catch (error: any) {
      hapticError();
      setError(error.message);
      setRetryCount(prev => prev + 1);
      
      toast({
        title: "Setup failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setIsRetrying(false);
    }
  };

  const handleManualRetry = () => {
    setError("");
    handleSubmit(new Event('submit') as any);
  };

  const steps = [
    { key: 'profile' as SetupStep, label: 'Profile' },
    { key: 'page' as SetupStep, label: 'Feedback Page' },
    { key: 'notifications' as SetupStep, label: 'Notifications' },
  ];

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-center text-2xl">Welcome to Sendfeedback!</DialogTitle>
          <DialogDescription className="text-center">
            Let's get started by setting up your company profile. We'll automatically create your first feedback page for you.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="space-y-3 py-2">
            {steps.map((step, index) => {
              const isComplete = currentStep === 'complete' || (
                steps.findIndex(s => s.key === currentStep) > index
              );
              const isCurrent = currentStep === step.key;
              
              return (
                <div key={step.key} className="flex items-center gap-3">
                  {isComplete ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  ) : isCurrent ? (
                    <Loader2 className="h-5 w-5 text-primary animate-spin flex-shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  )}
                  <span className={cn(
                    "text-sm transition-colors",
                    isComplete && "text-green-600",
                    isCurrent && "text-foreground font-medium",
                    !isComplete && !isCurrent && "text-muted-foreground"
                  )}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="setup-company-name">Company Name *</Label>
            <Input
              id="setup-company-name"
              type="text"
              placeholder="Acme Inc."
              value={companyName}
              onChange={(e) => {
                setCompanyName(e.target.value);
                setError("");
              }}
              disabled={loading}
              required
              maxLength={100}
              autoFocus={!isMobileDevice()}
              className="min-h-[48px] text-base"
            />
            {error && (
              <div className="space-y-2">
                <p className="text-sm text-destructive">{error}</p>
                {retryCount > 0 && !loading && (
                  <SecondaryButton
                    type="button"
                    onClick={handleManualRetry}
                    className="w-full"
                  >
                    Try Again
                  </SecondaryButton>
                )}
              </div>
            )}
          </div>

          <PrimaryButton type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {isRetrying ? "Retrying..." : "Setting up..."}
              </>
            ) : (
              "Continue to Dashboard"
            )}
          </PrimaryButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};
